import type { ComponentType, SVGProps } from "react";
import DefaultFile from "~icons/vscode-icons/default-file";
import DefaultFolder from "~icons/vscode-icons/default-folder";
import DefaultFolderOpened from "~icons/vscode-icons/default-folder-opened";
import FileTypeAstro from "~icons/vscode-icons/file-type-astro";
import FileTypeAstroconfig from "~icons/vscode-icons/file-type-astroconfig";
import FileTypeCss from "~icons/vscode-icons/file-type-css";
import FileTypeDotenv from "~icons/vscode-icons/file-type-dotenv";
import FileTypeFont from "~icons/vscode-icons/file-type-font";
import FileTypeGit from "~icons/vscode-icons/file-type-git";
import FileTypeHtml from "~icons/vscode-icons/file-type-html";
import FileTypeImage from "~icons/vscode-icons/file-type-image";
import FileTypeJs from "~icons/vscode-icons/file-type-js";
import FileTypeJson from "~icons/vscode-icons/file-type-json";
import FileTypeMarkdown from "~icons/vscode-icons/file-type-markdown";
import FileTypeMdx from "~icons/vscode-icons/file-type-mdx";
import FileTypeNpm from "~icons/vscode-icons/file-type-npm";
import FileTypeReactjs from "~icons/vscode-icons/file-type-reactjs";
import FileTypeReactts from "~icons/vscode-icons/file-type-reactts";
import FileTypeScss from "~icons/vscode-icons/file-type-scss";
import FileTypeSvg from "~icons/vscode-icons/file-type-svg";
import FileTypeToml from "~icons/vscode-icons/file-type-toml";
import FileTypeTsconfig from "~icons/vscode-icons/file-type-tsconfig";
import FileTypeTypescript from "~icons/vscode-icons/file-type-typescript";
import FileTypeTypescriptdef from "~icons/vscode-icons/file-type-typescriptdef";
import FileTypeVite from "~icons/vscode-icons/file-type-vite";
import FileTypeXml from "~icons/vscode-icons/file-type-xml";
import FileTypeYaml from "~icons/vscode-icons/file-type-yaml";
import FolderTypeAsset from "~icons/vscode-icons/folder-type-asset";
import FolderTypeAssetOpened from "~icons/vscode-icons/folder-type-asset-opened";
import FolderTypeAstro from "~icons/vscode-icons/folder-type-astro";
import FolderTypeAstroOpened from "~icons/vscode-icons/folder-type-astro-opened";
import FolderTypeComponent from "~icons/vscode-icons/folder-type-component";
import FolderTypeComponentOpened from "~icons/vscode-icons/folder-type-component-opened";
import FolderTypeImages from "~icons/vscode-icons/folder-type-images";
import FolderTypeImagesOpened from "~icons/vscode-icons/folder-type-images-opened";
import FolderTypePublic from "~icons/vscode-icons/folder-type-public";
import FolderTypePublicOpened from "~icons/vscode-icons/folder-type-public-opened";
import FolderTypeSrc from "~icons/vscode-icons/folder-type-src";
import FolderTypeSrcOpened from "~icons/vscode-icons/folder-type-src-opened";
import FolderTypeStyle from "~icons/vscode-icons/folder-type-style";
import FolderTypeStyleOpened from "~icons/vscode-icons/folder-type-style-opened";
import FolderTypeView from "~icons/vscode-icons/folder-type-view";
import FolderTypeViewOpened from "~icons/vscode-icons/folder-type-view-opened";

type FileIcon = ComponentType<SVGProps<SVGSVGElement>>;

function baseName(name: string): string {
	return name.split("/").pop() ?? name;
}

function specialFileIcon(fileName: string): FileIcon | null {
	const lower = fileName.toLowerCase();
	if (lower === "package.json" || lower === "package-lock.json") {
		return FileTypeNpm;
	}
	if (lower === "tsconfig.json" || lower.startsWith("tsconfig.")) {
		return FileTypeTsconfig;
	}
	if (lower.startsWith("astro.config.")) return FileTypeAstroconfig;
	if (lower.startsWith("vite.config.")) return FileTypeVite;
	if (lower === ".gitignore" || lower === ".gitattributes") return FileTypeGit;
	if (lower === ".env" || lower.startsWith(".env.")) return FileTypeDotenv;
	if (lower.endsWith(".d.ts")) return FileTypeTypescriptdef;
	return null;
}

function extensionIcon(fileName: string): FileIcon {
	const lower = fileName.toLowerCase();
	const ext = lower.includes(".")
		? lower.slice(lower.lastIndexOf(".") + 1)
		: "";
	switch (ext) {
		case "astro":
			return FileTypeAstro;
		case "tsx":
			return FileTypeReactts;
		case "ts":
			return FileTypeTypescript;
		case "jsx":
			return FileTypeReactjs;
		case "js":
		case "mjs":
		case "cjs":
			return FileTypeJs;
		case "css":
			return FileTypeCss;
		case "scss":
			return FileTypeScss;
		case "html":
		case "htm":
			return FileTypeHtml;
		case "json":
		case "jsonc":
			return FileTypeJson;
		case "md":
			return FileTypeMarkdown;
		case "mdx":
			return FileTypeMdx;
		case "svg":
			return FileTypeSvg;
		case "png":
		case "jpg":
		case "jpeg":
		case "gif":
		case "webp":
		case "ico":
		case "avif":
			return FileTypeImage;
		case "woff":
		case "woff2":
		case "ttf":
		case "otf":
		case "eot":
			return FileTypeFont;
		case "xml":
			return FileTypeXml;
		case "yml":
		case "yaml":
			return FileTypeYaml;
		case "toml":
			return FileTypeToml;
		default:
			return DefaultFile;
	}
}

function folderIcon(name: string, open: boolean): FileIcon {
	switch (name.toLowerCase()) {
		case "src":
		case "source":
			return open ? FolderTypeSrcOpened : FolderTypeSrc;
		case "components":
		case "component":
			return open ? FolderTypeComponentOpened : FolderTypeComponent;
		case "pages":
		case "views":
			return open ? FolderTypeViewOpened : FolderTypeView;
		case "public":
		case "static":
			return open ? FolderTypePublicOpened : FolderTypePublic;
		case "images":
		case "img":
		case "media":
			return open ? FolderTypeImagesOpened : FolderTypeImages;
		case "styles":
		case "css":
		case "scss":
			return open ? FolderTypeStyleOpened : FolderTypeStyle;
		case "assets":
			return open ? FolderTypeAssetOpened : FolderTypeAsset;
		case "astro":
			return open ? FolderTypeAstroOpened : FolderTypeAstro;
		default:
			return open ? DefaultFolderOpened : DefaultFolder;
	}
}

export function FileGlyph({ name }: { name: string }) {
	const fileName = baseName(name);
	const Icon = specialFileIcon(fileName) ?? extensionIcon(fileName);
	return <Icon className="mc-ide-file-icon" aria-hidden />;
}

export function FolderGlyph({
	name,
	open,
}: {
	name: string;
	open: boolean;
}) {
	const Icon = folderIcon(name, open);
	return <Icon className="mc-ide-file-icon" aria-hidden />;
}
